import * as React from 'react';
import { Box, IconButton, ThemeProvider } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { customTimePickerTheme } from '@styles/components/mui-time-picker-styles.ts';
import { AddIcon, DeleteBlackIcon } from '@components/icons/svg-icon.tsx';
import {
  TimeRange,
  TimeRangePickerListProps
} from '@models/time-range-picker.model.ts';
import { useEffect, useState, useRef } from 'react';
import arrowRightIcon from '@assets/icons/arrow-rigth.svg';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import { Dayjs } from 'dayjs';

function TimeRangePickerList({
  updateData = [],
  onChange,
  mode = 'single',
  helperText,
  required = false
}: TimeRangePickerListProps) {
  const { t } = useTranslation();

  const [ranges, setRanges] = useState<TimeRange[]>(
    updateData.length > 0 ? updateData : [{ start: null, end: null }]
  );
  const [touched, setTouched] = useState<boolean[]>(
    updateData.length > 0 ? updateData.map(() => false) : [false]
  );
  const [wasOpened, setWasOpened] = useState<boolean[]>(
    updateData.map(() => false)
  );
  const [isActive, setIsActive] = useState(false);
  const blurTimeout = useRef<(NodeJS.Timeout | null)[]>(
    updateData.map(() => null)
  );

  useEffect(() => {
    if (updateData.length > 0) {
      setRanges(updateData);
      setTouched(updateData.map(() => false));
      setWasOpened(updateData.map(() => false));
      blurTimeout.current = updateData.map(() => null);
    }
  }, [updateData]);

  const handleChange = (
    index: number,
    type: 'start' | 'end',
    value: Dayjs | null
  ) => {
    const newRanges = [...ranges];
    newRanges[index][type] = value;

    const sorted = newRanges
      .filter((r) => r.start && r.end)
      .sort((a, b) => (a.start?.isBefore(b.start!) ? -1 : 1));
    const unsorted = newRanges.filter((r) => !(r.start && r.end));

    const updated = [...sorted, ...unsorted];
    setRanges(updated);
    onChange?.(updated);

    if (updated[index].start && updated[index].end) {
      const newTouched = [...touched];
      newTouched[index] = false;
      setTouched(newTouched);
    }
  };

  const addRange = () => {
    setRanges([...ranges, { start: null, end: null }]);
    setTouched([...touched, false]);
    setWasOpened([...wasOpened, false]);
    blurTimeout.current.push(null);
  };

  const deleteRange = (index: number) => {
    const updated = ranges.filter((_, i) => i !== index);
    setRanges(updated);
    setTouched(touched.filter((_, i) => i !== index));
    setWasOpened(wasOpened.filter((_, i) => i !== index));
    blurTimeout.current.splice(index, 1);
    onChange?.(updated);
  };

  const handleTouch = (index: number) => {
    const range = ranges[index];
    if (!range.start || !range.end) {
      const newTouched = [...touched];
      newTouched[index] = true;
      setTouched(newTouched);
    }
  };

  const handleBlur = (index: number) => {
    setIsActive(false);
    blurTimeout.current[index] = setTimeout(() => {
      const range = ranges[index];
      if (!range.start || !range.end) {
        const newTouched = [...touched];
        newTouched[index] = true;
        setTouched(newTouched);
      }
    }, 150);
  };

  const handleFocus = (index: number) => {
    setIsActive(true);
    if (blurTimeout.current[index]) clearTimeout(blurTimeout.current[index]!);
  };

  const hasError = ranges.some((range, index) => {
    const isEmpty = required && !(range.start && range.end);
    return touched[index] && isEmpty;
  });

  return (
    <ThemeProvider theme={customTimePickerTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          className="flex"
          sx={{
            fontSize: '18px',
            '@media (max-width: 1715px)': { fontSize: '14px' },
            marginBottom: '6px',
            paddingLeft: '5px',
            color: hasError
              ? 'red'
              : isActive
                ? 'var(--primary)'
                : 'var(--dark-1)'
          }}
        >
          {required && (
            <span
              className="reuqired-symbole"
              style={{
                color: hasError
                  ? 'red'
                  : isActive
                    ? 'var(--primary)'
                    : 'var(--dark-1)'
              }}
            >
              *
            </span>
          )}
          {t('label_working_time')}
          <InfoTooltip text="tooltip_info_working_time" />
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          {ranges.map((range, index) => {
            const isEmpty = required && !(range.start && range.end);
            const showError = touched[index] && isEmpty;

            return (
              <Box key={index}>
                <div className="flex justify-between items-center gap-2">
                  <TimePicker
                    value={range.start}
                    onChange={(newVal) => handleChange(index, 'start', newVal)}
                    onOpen={() => {
                      setIsActive(true);
                      const newWasOpened = [...wasOpened];
                      newWasOpened[index] = true;
                      setWasOpened(newWasOpened);
                    }}
                    onClose={() => {
                      setIsActive(false);
                      handleTouch(index);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: showError,
                        onFocus: () => handleFocus(index),
                        onBlur: () => handleBlur(index)
                      }
                    }}
                  />

                  <img src={arrowRightIcon} alt="arrowRightIcon" />

                  <TimePicker
                    value={range.end}
                    onChange={(newVal) => handleChange(index, 'end', newVal)}
                    onOpen={() => {
                      setIsActive(true);
                      const newWasOpened = [...wasOpened];
                      newWasOpened[index] = true;
                      setWasOpened(newWasOpened);
                    }}
                    onClose={() => {
                      setIsActive(false);
                      handleTouch(index);
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: showError,
                        onFocus: () => handleFocus(index),
                        onBlur: () => handleBlur(index)
                      }
                    }}
                  />

                  {mode === 'multiple' &&
                    (index === 0 ? (
                      <IconButton onClick={addRange}>
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() => deleteRange(index)}
                        color={'error'}
                      >
                        <DeleteBlackIcon />
                      </IconButton>
                    ))}
                </div>

                <div className="flex justify-start items-start min-h-[20px] max-lg-xl:min-h-[16px]">
                  <span
                    className={`!ml-[14px] !text-red-500 !text-[14px] max-lg-xl:!text-[12px] transition-all duration-300 ease-in-out transform origin-top ${
                      showError
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 -translate-y-1 pointer-events-none'
                    }`}
                  >
                    {helperText}
                  </span>
                </div>
              </Box>
            );
          })}
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default TimeRangePickerList;
